import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { addNotification } from '../../store/slices/uiSlice';
import api from '../../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const TitheReceiptForm = ({ onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [bankOptions, setBankOptions] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  
  // Form data state
  const [formData, setFormData] = useState({
    chinese_name: '',
    english_name: '',
    contact_phone: '',
    email: '',
    worship_code: '',
    address: '',
    district: '',
    area: '',
    receiving_bank: '',
    transaction_type: '',
    deposit_date: new Date(),
    reference_number: '',
    tithe_amount: '',
    thanksgiving_offering: '',
    church_operating: '',
    building_fund: '',
    gospel_ministry: '',
    love_neighbor_fund: '',
    special_offering: '',
    special_offering_description: '',
    other_offering: '',
    other_offering_description: '',
    receipt_required: true,
    receipt_frequency: 'independent',
    receipt_language: 'zh',
    remarks: ''
  });

  const [receiptFile, setReceiptFile] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    fetchBankOptions();
    fetchTransactionTypes();
  }, []);

  // Calculate total amount whenever individual amounts change
  useEffect(() => {
    const amounts = [
      parseFloat(formData.tithe_amount) || 0,
      parseFloat(formData.thanksgiving_offering) || 0,
      parseFloat(formData.church_operating) || 0,
      parseFloat(formData.building_fund) || 0,
      parseFloat(formData.gospel_ministry) || 0,
      parseFloat(formData.love_neighbor_fund) || 0,
      parseFloat(formData.special_offering) || 0,
      parseFloat(formData.other_offering) || 0
    ];
    const total = amounts.reduce((sum, amount) => sum + amount, 0);
    setTotalAmount(total);
  }, [formData.tithe_amount, formData.thanksgiving_offering, formData.church_operating, formData.building_fund, formData.gospel_ministry, formData.love_neighbor_fund, formData.special_offering, formData.other_offering]);

  const fetchBankOptions = async () => {
    try {
      const response = await api.get('/api/v1/tithe-receipts/options/banks');
      setBankOptions(response.data.banks || response.data);
    } catch (error) {
      console.error('Error fetching bank options:', error);
    }
  };

  const fetchTransactionTypes = async () => {
    try {
      const response = await api.get('/api/v1/tithe-receipts/options/transaction-types');
      setTransactionTypes(response.data.types || response.data);
    } catch (error) {
      console.error('Error fetching transaction types:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      deposit_date: date
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        dispatch(addNotification({
          type: 'error',
          title: 'Invalid File Type',
          message: 'Please upload a PDF, JPEG, or PNG file'
        }));
        return;
      }

      if (file.size > maxSize) {
        dispatch(addNotification({
          type: 'error',
          title: 'File Too Large',
          message: 'Please upload a file smaller than 5MB'
        }));
        return;
      }

      setReceiptFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.chinese_name || !formData.contact_phone) {
      dispatch(addNotification({
        type: 'error',
        title: t('common.errorTitle'),
        message: t('titheReceipts.form.validationRequired')
      }));
      return;
    }

    if (totalAmount <= 0) {
      dispatch(addNotification({
        type: 'error',
        title: t('common.errorTitle'),
        message: t('titheReceipts.form.validationAmount')
      }));
      return;
    }

    setLoading(true);

    try {
      // Prepare form data for submission
      const submitData = {
        ...formData,
        deposit_date: formData.deposit_date.toISOString().split('T')[0], // Format as YYYY-MM-DD
        total_amount: totalAmount.toFixed(2)
      };

      // Convert string amounts to numbers
      ['tithe_amount', 'thanksgiving_offering', 'church_operating', 'building_fund', 'gospel_ministry', 'love_neighbor_fund', 'special_offering', 'other_offering'].forEach(field => {
        submitData[field] = parseFloat(submitData[field]) || 0;
      });

      // Create the receipt
      const response = await api.post('/api/v1/tithe-receipts/', submitData);

      // Upload file if provided
      if (receiptFile) {
        const fileFormData = new FormData();
        fileFormData.append('file', receiptFile);

        await api.post(`/api/v1/tithe-receipts/${response.data.id}/upload-receipt`, fileFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      dispatch(addNotification({
        type: 'success',
        title: t('common.successTitle'),
        message: t('titheReceipts.form.createdSuccess', { id: response.data.id })
      }));

      if (onSuccess) {
        onSuccess(response.data);
      }

      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error creating tithe receipt:', error);
      dispatch(addNotification({
        type: 'error',
        title: t('common.errorTitle'),
        message: error.response?.data?.detail || t('titheReceipts.form.createError')
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">{t('titheReceipts.form.createTitle')}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('titheReceipts.form.personalInfo')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.chineseName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="chinese_name"
                  value={formData.chinese_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.englishName')}
                </label>
                <input
                  type="text"
                  name="english_name"
                  value={formData.english_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.contactPhone')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.email')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.worshipCode')}
                </label>
                <input
                  type="text"
                  name="worship_code"
                  value={formData.worship_code}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* {t('titheReceipts.form.address')} Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('titheReceipts.form.addressInfo')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.address')}
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.district')}
                </label>
                <input
                  type="text"
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.area')}
                </label>
                <input
                  type="text"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('titheReceipts.form.paymentInfo')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.receivingBank')}
                </label>
                <select
                  name="receiving_bank"
                  value={formData.receiving_bank}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('titheReceipts.form.receivingBank')}</option>
                  {bankOptions.map(bank => (
                    <option key={bank} value={bank}>{bank}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.transactionType')}
                </label>
                <select
                  name="transaction_type"
                  value={formData.transaction_type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('titheReceipts.form.transactionType')}</option>
                  {transactionTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.depositDate')}
                </label>
                <DatePicker
                  selected={formData.deposit_date}
                  onChange={handleDateChange}
                  dateFormat="yyyy-MM-dd"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.referenceNumber')}
                </label>
                <input
                  type="text"
                  name="reference_number"
                  value={formData.reference_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Donation Amounts */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('titheReceipts.form.donationBreakdown')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.tithe')} (HK$)
                </label>
                <input
                  type="number"
                  name="tithe_amount"
                  value={formData.tithe_amount}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.thanksgiving')} (HK$)
                </label>
                <input
                  type="number"
                  name="thanksgiving_offering"
                  value={formData.thanksgiving_offering}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.churchOperating')} (HK$)
                </label>
                <input
                  type="number"
                  name="church_operating"
                  value={formData.church_operating}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.buildingFund')} (HK$)
                </label>
                <input
                  type="number"
                  name="building_fund"
                  value={formData.building_fund}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.gospelMinistry')} (HK$)
                </label>
                <input
                  type="number"
                  name="gospel_ministry"
                  value={formData.gospel_ministry}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.loveNeighbor')} (HK$)
                </label>
                <input
                  type="number"
                  name="love_neighbor_fund"
                  value={formData.love_neighbor_fund}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.specialOffering')} (HK$)
                </label>
                <input
                  type="number"
                  name="special_offering"
                  value={formData.special_offering}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-end">
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('titheReceipts.form.totalAmount')}
                  </label>
                  <div className="text-2xl font-bold text-green-600 px-3 py-2 bg-white border border-gray-300 rounded-md">
                    HK${totalAmount.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Special Offering Description */}
            {parseFloat(formData.special_offering) > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.specialOfferingDesc')}
                </label>
                <textarea
                  name="special_offering_description"
                  value={formData.special_offering_description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder=""
                />
              </div>
            )}

            {/* Other offering + free text */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.otherOffering')} (HK$)
                </label>
                <input
                  type="number"
                  name="other_offering"
                  value={formData.other_offering}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.otherOfferingDesc')}
                </label>
                <input
                  type="text"
                  name="other_offering_description"
                  value={formData.other_offering_description}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('titheReceipts.form.otherOfferingPlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Receipt Options */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('titheReceipts.form.receiptOptions')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="receipt_required"
                    checked={formData.receipt_required}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">{t('titheReceipts.form.receiptRequired')}</span>
                </label>
              </div>
              <div>
                <p className="block text-sm font-medium text-gray-700 mb-2">{t('titheReceipts.form.receiptType')}</p>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="receipt_frequency"
                      value="independent"
                      checked={formData.receipt_frequency === 'independent'}
                      onChange={handleInputChange}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span>{t('titheReceipts.form.receiptIndependent')}</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="receipt_frequency"
                      value="yearly"
                      checked={formData.receipt_frequency === 'yearly'}
                      onChange={handleInputChange}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span>{t('titheReceipts.form.receiptYearly')}</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheReceipts.form.receiptLanguage')}
                </label>
                <select
                  name="receipt_language"
                  value={formData.receipt_language}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="zh">中文</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('titheReceipts.form.uploadReceipt')}</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('titheReceipts.form.selectFile')}
              </label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {receiptFile && (
                <p className="text-sm text-green-600 mt-1">
                  {t('titheReceipts.form.selectedFile', { name: receiptFile.name })}
                </p>
              )}
            </div>
          </div>

          {/* {t('titheReceipts.form.remarks')} */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('titheReceipts.form.remarks')}</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('titheReceipts.form.remarks')}
              </label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder=""
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded"
            >
              {t('titheReceipts.form.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded disabled:opacity-50"
            >
              {loading ? t('titheReceipts.form.creating') : t('titheReceipts.form.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TitheReceiptForm;
